import { FC, useRef, useEffect, useCallback, useState } from 'react'
import { styled } from '@portfolio/styles'
import { useVisibleChapterId, useScrollContents } from '@portfolio/article'
import { SectionArticleContentsProps } from '@portfolio/models'
import { useTranslation } from 'react-i18next'
import { Link, KlaviyoForm } from '@portfolio/components'

const CTA_FORM_RESERVED_HEIGHT = 211
const TOP_OFFSET = 48
const BOTTOM_OFFSET = 24

const Wrap = styled('div', {
  display: 'none',

  '@lg': {
    background: '$white',
    zIndex: '$header',
    display: 'flex',
    flexDirection: 'column',
    gap: '$72',
    position: 'sticky',
    top: TOP_OFFSET,
    marginBottom: BOTTOM_OFFSET,
    maxHeight: `calc(100vh - ${TOP_OFFSET}px - ${BOTTOM_OFFSET}px)`,
    height: 'fit-content',
    maxWidth: '360px',
  },
})

const Chapter = styled(Link, {
  projectFont: 'ui04',
  color: '$black_05',
  display: 'grid',
  gridTemplateColumns: '18px 1fr',
  gap: '$4',
  whiteSpace: 'nowrap',
  variants: {
    active: {
      true: {
        color: '$black',
      },
    },
  },

  '@lg': {
    whiteSpace: 'unset',
  },
})

const ChaptersWrap = styled('div', {
  display: 'none',

  '@lg': {
    display: 'flex',
    flexDirection: 'column',
    gap: '$8',
    containerSpace: 'none',
    paddingTop: '$0',
    paddingBottom: '$0',
  },
})
const SliderWrap = styled('div', {
  display: 'none',

  '@lg': {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    minHeight: 0,
    containerSpace: 'medium',
    width: '100%',

    '&::-webkit-scrollbar': {
      width: '$6',
      height: '$6',
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '$gray50',
      borderRadius: '$rMax',
    },
  },
  variants: {
    isSticky: {
      true: {
        boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.1)',

        '@lg': {
          boxShadow: 'none',
        },
      },
    },
  },
})
const ContentsTitle = styled('h2', {
  projectFont: 'ui03',
  textTransform: 'uppercase',
  marginBottom: '$20',
  display: 'none',
  '@lg': {
    display: 'block',
  },
})

const CtaWrap = styled('div', {
  '@lg': {
    display: 'block',
    flex: '0 0 auto',
    marginTop: '$24',
    containerSpace: 'medium',
    paddingRight: 0,
  },
})

const CtaBox = styled('aside', {
  backgroundColor: '$gray50',
  borderRadius: '$rBoxes',
  padding: '$24',
  color: '$textPrimaryBlack',
  width: '100%',
  maxWidth: '330px',
  marginInline: 'auto',

  '@md': {
    marginInline: 'unset',
  },
})

const CtaTitle = styled('h3', {
  projectFont: 'heading07',
  marginBottom: '$24',
})

const CtaFormArea = styled('div', {
  position: 'relative',
  // Reserve height so the sidebar doesn't jump while the Klaviyo form loads.
  minHeight: CTA_FORM_RESERVED_HEIGHT,
})

const CtaDisclaimer = styled('p', {
  projectFont: 'ui04',
  fontSize: '11px',
  color: '$gray300',
  marginTop: '$12',
})

export const SectionArticleContents: FC<SectionArticleContentsProps> = ({
  contents,
  cta,
}) => {
  const { t } = useTranslation('article')
  const visibleChapterId = useVisibleChapterId()
  const MIN_SHIFT_LEFT = 20
  const [activeSlideScrollPos, setAtiveSlideScrollPos] = useState<
    number | undefined
  >(undefined)
  const [isSticky, setIsSticky] = useState<boolean>(false)

  const sliderRef = useRef<HTMLDivElement>(null)
  const sliderEl = sliderRef.current
  const activeChapterElementRef = useRef<HTMLAnchorElement | null>(null)

  const { title, klaviyoFormId, disclaimer } = cta || {}

  const showCtaBox = Boolean(cta)

  /*   Callback used every time when set active slide ref
  that set position to scroll when slide is active */
  const activeSlideRef = useCallback<(node: HTMLAnchorElement) => void>(
    (node) => {
      if (node !== null) {
        activeChapterElementRef.current = node

        const { offsetLeft } = node
        const { width } = node.getBoundingClientRect()
        const windowWidth = window.innerWidth

        const shiftToCenter =
          (windowWidth - width) / 2 > 0
            ? (windowWidth - width) / 2
            : MIN_SHIFT_LEFT

        setAtiveSlideScrollPos(offsetLeft - shiftToCenter)
      }
    },
    [],
  )

  // Scroll to currently active chapter (horizontal — mobile)
  useEffect(() => {
    if (activeSlideScrollPos && sliderEl) {
      sliderEl.scroll({
        left: activeSlideScrollPos,
      })
    }
  }, [activeSlideScrollPos, sliderEl])

  useScrollContents({
    activeChapterElementRef,
    sliderRef,
    visibleChapterId,
    firstChapterTitle: contents?.[0]?.chapterTitle,
  })

  // Check that element is sticky for shadow
  useEffect(() => {
    if (!sliderRef.current) return

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0]
        if (entry.intersectionRatio < 1) {
          setIsSticky(true)
        } else {
          setIsSticky(false)
        }
      },
      {
        root: null,
        threshold: [1],
      },
    )

    observer.observe(sliderRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  // Add smooth scroll in that way because we want to have scroll behavior only for chapter links and not when user click link which navigates to the another page.
  const addSmoothScroll = () => {
    const html = document.querySelector('html')

    html?.style.setProperty('scroll-behavior', 'smooth')
    setTimeout(() => html?.style.removeProperty('scroll-behavior'), 0)
  }

  return (
    <Wrap>
      <SliderWrap ref={sliderRef} isSticky={isSticky}>
        <ContentsTitle>{t('contents')}</ContentsTitle>
        <ChaptersWrap>
          {contents?.map((chapter, index) => {
            return (
              <Chapter
                data-active={chapter.chapterTitle === visibleChapterId}
                active={chapter.chapterTitle === visibleChapterId}
                key={`${chapter.chapterTitle}-${index}`}
                href={`#${chapter.chapterTitle}`}
                onClick={addSmoothScroll}
                ref={
                  chapter.chapterTitle === visibleChapterId
                    ? activeSlideRef
                    : undefined
                }
              >
                <span>{index + 1}.</span>
                {chapter.chapterTitle}
              </Chapter>
            )
          })}
        </ChaptersWrap>
      </SliderWrap>
      {showCtaBox && (
        <CtaWrap>
          <CtaBox>
            {title && <CtaTitle>{title}</CtaTitle>}
            <CtaFormArea aria-live="polite">
              <KlaviyoForm formId={klaviyoFormId} />
            </CtaFormArea>
            {disclaimer && <CtaDisclaimer>{disclaimer}</CtaDisclaimer>}
          </CtaBox>
        </CtaWrap>
      )}
    </Wrap>
  )
}
